import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { TRADING_NAME } from "@/constants/company";

// eslint-disable-next-line react-refresh/only-export-components
export function scrollToPageTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

type HelioHomeLinkProps = {
  className?: string;
  children: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export function HelioHomeLink({ className, onClick, children }: HelioHomeLinkProps) {
  return (
    <Link
      href="/"
      aria-label={`${TRADING_NAME} home`}
      className={cn("inline-flex hover:opacity-90 transition-opacity", className)}
      onClick={(event) => {
        scrollToPageTop();
        onClick?.(event);
      }}
    >
      {children}
    </Link>
  );
}

/** Powlo wordmark. Height follows `font-size` / text-* classes via `1em`. */
function HelioLogoSvg({ className, ...props }: React.ComponentPropsWithoutRef<"svg">) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="8.898 -3.972 1565.945 524.05"
      role="img"
      aria-label={TRADING_NAME}
      className={cn("inline-block h-[1.5em] w-auto md:h-[2em]", className)}
      {...props}
    >
      <g transform="matrix(1, 0, 0, 1, -90.120972, -3325.971924)">
        <path
          fill="currentColor"
          d="M 1516.44 3465.86 C 1593.14 3460.61 1659.53 3518.59 1664.65 3595.3 C 1669.77 3672 1611.67 3738.29 1534.96 3743.28 C 1458.44 3748.27 1392.33 3690.33 1387.22 3613.82 C 1382.11 3537.29 1439.93 3471.09 1516.44 3465.86 Z M 1446.82 3616.51 C 1453.18 3659.99 1493.51 3690.14 1537.01 3683.93 C 1580.73 3677.69 1611.05 3637.12 1604.66 3593.42 C 1598.27 3549.73 1557.6 3519.54 1513.93 3526.08 C 1470.47 3532.59 1440.46 3573.03 1446.82 3616.51 Z"
        />
        <path
          fill="currentColor"
          d="M 592.358 3466.13 C 668.854 3460.87 735.104 3518.68 740.241 3595.19 C 745.378 3671.7 687.453 3737.85 610.939 3742.86 C 534.606 3747.86 468.644 3690.1 463.519 3613.77 C 458.394 3537.45 516.042 3471.39 592.358 3466.13 Z M 523.154 3616.1 C 529.345 3659.52 569.545 3689.72 612.969 3683.56 C 656.442 3677.4 686.675 3637.14 680.477 3593.67 C 674.279 3550.2 634 3520 590.537 3526.23 C 547.123 3532.46 516.963 3572.69 523.154 3616.1 Z"
        />
        <path
          fill="currentColor"
          d="M 1176.42 3482.7 L 1235.42 3482.84 C 1235.45 3522.01 1236.45 3565.06 1235.38 3603.9 C 1234.08 3641.95 1217.89 3677.97 1190.29 3704.21 C 1163.97 3729.21 1128.78 3742.72 1092.48 3741.75 C 1053.4 3740.51 1022.16 3723.98 995.79 3695.97 C 994.34 3697.48 992.87 3698.98 991.4 3700.47 C 965.03 3727.08 930.41 3743.14 892.66 3743.24 C 856.7 3743.24 822.18 3729.05 796.628 3703.74 C 754.693 3661.94 758.301 3617.94 758.298 3564.67 L 758.339 3482.82 C 777.936 3482.57 798.181 3482.92 817.82 3483.03 L 817.79 3566.68 C 817.78 3583.41 816.72 3613.4 820.31 3628.25 C 823.21 3640.49 829.37 3651.72 838.14 3660.73 C 867.83 3691.13 915.99 3690.61 945.98 3660.8 C 955.1 3651.77 961.67 3640.49 965.02 3628.1 C 968.77 3613.69 967.59 3582.77 967.59 3566.64 L 967.56 3482.79 C 987.44 3482.74 1007.31 3482.8 1027.18 3482.99 L 1027.06 3567.83 C 1027.06 3583.94 1025.9 3616.06 1029.45 3630.79 C 1032.2 3642.11 1037.94 3652.48 1046.06 3660.82 C 1059.76 3674.78 1077.71 3681.69 1097.17 3681.77 C 1118.02 3681.76 1138.01 3673.45 1152.73 3658.67 C 1178.92 3632.49 1176.5 3604.68 1176.48 3570.95 L 1176.39 3517.08 L 1176.42 3482.7 Z"
        />
        <path
          fill="currentColor"
          transform="matrix(-1, 0, 0, -1, 2641.600046, 7072.100046)"
          d="M 1325.02 3750.1 L 1384.02 3749.96 C 1384.05 3710.79 1386.47 3498.4 1385.4 3459.56 C 1384.1 3421.51 1367.91 3385.49 1340.31 3359.25 C 1317.25 3337.35 1287.38 3324.27 1255.92 3322.01 L 1255.92 3382.19 C 1273.56 3384.17 1290.1 3392.09 1302.75 3404.79 C 1328.94 3430.97 1326.52 3458.78 1326.5 3492.51 L 1326.41 3546.38 L 1325.02 3750.1 Z"
        />
        <path
          fill="currentColor"
          d="M 183.406 3808.81 C 160.349 3830.71 130.479 3843.79 99.019 3846.05 L 99.019 3785.87 C 116.659 3783.89 133.199 3775.97 145.849 3763.27 C 165.787 3743.34 169.144 3722.46 169.602 3698.75 L 169.573 3678.84 C 169.545 3654.01 167.929 3598.26 172.473 3576.63 C 177.262 3554.58 187.451 3534.07 202.127 3516.93 C 228.853 3485.43 262.231 3468.35 303.179 3465.01 C 305.521 3464.57 310.557 3464.6 313.023 3464.72 C 391.064 3468.63 448.351 3531.93 446.749 3609.41 C 445.896 3645.61 430.689 3679.98 404.477 3704.96 C 364.469 3743.06 307.906 3755.35 256.366 3734.46 C 250.546 3732.1 232.974 3722.3 229.681 3720.43 L 227.749 3719.06 C 224.084 3753.17 208.438 3785.01 183.406 3808.81 Z M 229.158 3614.67 C 234.332 3658.49 274.16 3689.75 317.957 3684.36 C 361.447 3679 392.437 3639.51 387.299 3596 C 382.162 3552.48 342.828 3521.29 299.287 3526.21 C 255.439 3531.17 223.984 3570.84 229.158 3614.67 Z"
        />
      </g>
    </svg>
  );
}

type HelioLogoMarkProps = React.ComponentPropsWithoutRef<"span"> & {
  /** When true (default), wraps the mark in a link to the home page. */
  linked?: boolean;
};

export default function HelioLogoMark({ className, linked = true, style, ...props }: HelioLogoMarkProps) {
  const mark = (
    <span
      className={cn("inline-flex items-center leading-none text-xl", className)}
      style={style}
      {...props}
    >
      <HelioLogoSvg />
    </span>
  );

  if (!linked) {
    return mark;
  }

  return <HelioHomeLink>{mark}</HelioHomeLink>;
}
